import React from 'react';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  IconButton,
  Box,
  Grid,
  Button,
} from '@mui/material';
import {
  Add,
  Remove,
  Delete,
} from '@mui/icons-material';
import { useUpdateCartItem } from '../../../hooks/storefront/useUpdateCartItem';
import { useRemoveFromCart } from '../../../hooks/storefront/useRemoveFromCart';
import { formatMoney } from '../../../utils/money';

import { useTranslation } from 'react-i18next';

// Renders the per-line stock validation chip from the
// /cart/validate response. `stockIssue` may be:
//   { available: 0, ... }                  → out of stock
//   { available: <n less than item.qty> }  → only N left
//   { error / message }                    → backend-supplied text
const StockChip = ({ stockIssue, requestedQty, t }) => {
  if (!stockIssue) return null;
  const available = Number(stockIssue.available);
  const isOut = stockIssue.available === 0;
  const isShort = !isOut && Number.isFinite(available) && available > 0 && available < Number(requestedQty || 0);

  let label;
  if (isOut) {
    label = t('cart.outOfStock', 'Out of stock — remove to proceed');
  } else if (isShort) {
    label = t('cart.onlyNLeft', { defaultValue: 'Only {{n}} left', n: available });
  } else {
    label = stockIssue.message || stockIssue.error;
  }
  if (!label) return null;
  return (
    <Typography
      variant="caption"
      sx={{ display: 'inline-block', color: 'error.main', fontWeight: 700, mt: 0.5 }}
    >
      {label}
    </Typography>
  );
};

const CartItem = ({ item, currency, stockIssue }) => {
  const { t, i18n } = useTranslation();
  const lng = i18n.language || "en";
  const updateCartItem = useUpdateCartItem();
  const removeFromCart = useRemoveFromCart();

  const handleQuantityChange = (newQuantity) => {
    if (newQuantity < 1) return;

    updateCartItem.mutate({
      itemId: item.id,
      data: { qty: newQuantity }
    });
  };

  const handleRemove = () => {
    removeFromCart.mutate(item.id);
  };

  return (
    <Card sx={{ mb: 2, borderRadius: 2 }}>
      <CardContent sx={{ p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          {/* Product Image */}
          <Grid item xs={12} sm={3}>
            <CardMedia
              component="img"
              image={item.product?.cover_url || item.product?.image || '/placeholder-product.jpg'}
              alt={item.product?.name?.[lng] || item.product?.name?.en || 'Product'}
              sx={{
                width: '100%',
                height: 120,
                objectFit: 'cover',
                borderRadius: 1,
              }}
            />
          </Grid>

          {/* Product Details */}
          <Grid item xs={12} sm={6}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              {item.product?.name?.[lng] || item.product?.name?.en || 'Product Name'}
            </Typography>

            {item.product?.variant && (
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {item.product.variant}
              </Typography>
            )}

            <Typography variant="body2" color="text.secondary">
              {t('cart.unitPrice')}: {formatMoney(item.price || item.unit_price, currency)}
            </Typography>
            <StockChip stockIssue={stockIssue} requestedQty={item.qty} t={t} />
          </Grid>

          {/* Quantity and Actions */}
          <Grid item xs={12} sm={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <IconButton
                size="small"
                onClick={() => handleQuantityChange(item.qty - 1)}
                disabled={item.qty <= 1 || updateCartItem.isPending}
              >
                <Remove />
              </IconButton>

              <Typography variant="body1" sx={{ minWidth: 40, textAlign: 'center', fontWeight: 'bold' }}>
                {item.qty}
              </Typography>

              <IconButton
                size="small"
                onClick={() => handleQuantityChange(item.qty + 1)}
                disabled={updateCartItem.isPending}
              >
                <Add />
              </IconButton>
            </Box>

            <Typography variant="h6" color="primary" gutterBottom fontWeight="bold">
              {formatMoney(Number(item.price || item.unit_price) * Number(item.qty), currency)}
            </Typography>

            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<Delete />}
              onClick={handleRemove}
              disabled={removeFromCart.isPending}
              fullWidth
              sx={{ borderRadius: 1.5 }}
            >
              {t('cart.remove')}
            </Button>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default CartItem;
